/**
 * Generates fill-color paint property based on interpolation setting.
 * @param binColorPairs - Array of { value: number, color: string } pairs, where
 *   `value` is the UPPER edge of bin k (ascending), matching the labelling used
 *   by `buildLegendItems` (legend range for colour c_k is [v_{k-1}, v_k)).
 * @param interpolate - Whether to interpolate between colors (smooth) or use discrete steps
 * @param max - Optional configured max edge (matches the `max` passed to `buildLegendItems`
 *   for the same render). In discrete mode, thresholds are clamped to this value so the
 *   step expression's bucket boundaries agree with the legend's clamped labels.
 * @returns Mapbox paint property expression
 */
export const generateFillColorPaint = (
  binColorPairs: { value: number; color: string }[],
  interpolate: boolean,
  max?: Maybe<number>
): unknown => {
  if (binColorPairs.length === 0) {
    return "transparent";
  }

  if (binColorPairs.length === 1) {
    // A single bucket has no boundary to express. A zero-stop "step" expression is invalid
    // Mapbox style-spec syntax (requires at least one stop); a single-stop "interpolate" is
    // valid but renders a constant anyway. A constant colour is correct for both modes.
    return binColorPairs[0].color;
  }

  if (interpolate) {
    // Smooth interpolation using Mapbox interpolate expression
    return [
      "interpolate",
      ["linear"],
      ["get", "val"],
      ...binColorPairs.flatMap((pair) => [pair.value, pair.color])
    ];
  } else {
    // Discrete steps using Mapbox step expression
    // step expression format: ["step", input, defaultOutput, stop1, output1, stop2, output2, ...]
    // Each threshold (bin k's upper edge, v_k) is paired with the NEXT colour (c_{k+1}) so
    // that a value landing exactly on v_k is painted with the colour whose legend range
    // starts at v_k — matching buildLegendItems, where colour c_k labels [v_{k-1}, v_k).
    // The final threshold (v_{n-1}) is intentionally unused: values >= v_{n-2} all fall
    // into the last colour, c_{n-1}.
    //
    // `buildLegendItems` clamps any displayed value to `max` (e.g. when a `greaterThan`
    // edge is configured but bins are computed past it). Thresholds here must be clamped
    // the same way or the paint expression's buckets drift from the legend's labelled
    // ranges near `max`. Only clamp when doing so preserves strictly-ascending stops
    // (a hard Mapbox requirement) — normally only the last used threshold can exceed
    // `max`, but this is guarded defensively in case more than one does.
    let previousThreshold = -Infinity;
    const thresholds = binColorPairs.slice(0, -1).map((pair) => {
      const rawValue = pair.value;
      const clampedValue = max != null ? Math.min(rawValue, max) : rawValue;
      const threshold = clampedValue > previousThreshold ? clampedValue : rawValue;
      previousThreshold = threshold;
      return threshold;
    });

    return [
      "step",
      ["get", "val"],
      binColorPairs[0].color, // default color for values below first stop
      ...thresholds.flatMap((threshold, i) => [threshold, binColorPairs[i + 1].color])
    ];
  }
};
